import React, { useRef, useEffect, useCallback } from 'react';

/**
 * MobileControls — analog joystick (left) + vertical throttle lever (right)
 * Writes to controlsRef.current: { joyX, joyY, throttle }
 */
const MobileControls = ({ controlsRef }) => {
  const baseRef = useRef(null);
  const handleRef = useRef(null);
  const leverRef = useRef(null);
  const knobRef = useRef(null);
  const joyActive = useRef(false);
  const joyPointer = useRef(null);
  const levPointer = useRef(null);
  const JOY_R = 42;

  // ── Joystick ──
  const updateJoy = useCallback((cx, cy) => {
    const base = baseRef.current; if (!base) return;
    const rect = base.getBoundingClientRect();
    let dx = cx - (rect.left + rect.width / 2);
    let dy = cy - (rect.top + rect.height / 2);
    const dist = Math.hypot(dx, dy);
    if (dist > JOY_R) { dx = dx / dist * JOY_R; dy = dy / dist * JOY_R; }
    if (handleRef.current) handleRef.current.style.transform = `translate(${dx}px, ${dy}px)`;
    if (controlsRef) { controlsRef.current.joyX = dx / JOY_R; controlsRef.current.joyY = -dy / JOY_R; }
  }, [controlsRef]);

  const resetJoy = useCallback(() => {
    joyActive.current = false; joyPointer.current = null;
    if (handleRef.current) {
      // A short spring style ease on release only, so the return snap feels
      // bouncy while active dragging stays perfectly responsive with no lag.
      handleRef.current.style.transition = 'transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)';
      handleRef.current.style.transform = 'translate(0px,0px)';
      window.setTimeout(() => {
        if (handleRef.current) handleRef.current.style.transition = 'none';
      }, 360);
    }
    if (baseRef.current) baseRef.current.style.boxShadow = '';
    if (controlsRef) { controlsRef.current.joyX = 0; controlsRef.current.joyY = 0; }
  }, [controlsRef]);

  // ── Throttle lever ──
  const updateThrottle = useCallback((cy) => {
    const lev = leverRef.current; if (!lev) return;
    const rect = lev.getBoundingClientRect();
    const t = 1 - Math.max(0, Math.min(1, (cy - rect.top) / rect.height));
    if (knobRef.current) knobRef.current.style.bottom = `calc(${t * 100}% - 10px)`;
    if (controlsRef) controlsRef.current.throttle = t;
  }, [controlsRef]);

  useEffect(() => {
    const base = baseRef.current;
    const lev = leverRef.current;
    if (!base || !lev) return;

    // Joystick events
    const jDown = (e) => {
      e.preventDefault();
      joyActive.current = true; joyPointer.current = e.pointerId;
      try { base.setPointerCapture(e.pointerId); } catch (_) { }
      if (handleRef.current) handleRef.current.style.transition = 'none';
      base.style.boxShadow = '0 0 22px rgba(102,126,234,0.55)';
      updateJoy(e.clientX, e.clientY);
    };
    const jMove = (e) => { if (joyActive.current && e.pointerId === joyPointer.current) { e.preventDefault(); updateJoy(e.clientX, e.clientY); } };
    const jUp = (e) => { if (e.pointerId === joyPointer.current) resetJoy(); };

    base.addEventListener('pointerdown', jDown, { passive: false });
    base.addEventListener('pointermove', jMove, { passive: false });
    base.addEventListener('pointerup', jUp);
    base.addEventListener('pointercancel', resetJoy);

    // Throttle lever events
    const lDown = (e) => {
      e.preventDefault(); levPointer.current = e.pointerId;
      try { lev.setPointerCapture(e.pointerId); } catch (_) { }
      lev.style.boxShadow = '0 0 22px rgba(240,147,251,0.5)';
      updateThrottle(e.clientY);
    };
    const lMove = (e) => { if (e.pointerId === levPointer.current) { e.preventDefault(); updateThrottle(e.clientY); } };
    const lUp = () => {
      levPointer.current = null;
      if (leverRef.current) leverRef.current.style.boxShadow = '';
    };

    lev.addEventListener('pointerdown', lDown, { passive: false });
    lev.addEventListener('pointermove', lMove, { passive: false });
    lev.addEventListener('pointerup', lUp);
    lev.addEventListener('pointercancel', lUp);

    // Set initial knob position
    if (knobRef.current) knobRef.current.style.bottom = `calc(45% - 10px)`;

    return () => {
      base.removeEventListener('pointerdown', jDown);
      base.removeEventListener('pointermove', jMove);
      base.removeEventListener('pointerup', jUp);
      base.removeEventListener('pointercancel', resetJoy);
      lev.removeEventListener('pointerdown', lDown);
      lev.removeEventListener('pointermove', lMove);
      lev.removeEventListener('pointerup', lUp);
      lev.removeEventListener('pointercancel', lUp);
    };
  }, [updateJoy, resetJoy, updateThrottle]);

  return (
    <div className="mobile-controls" aria-hidden="true">
      {/* Joystick — left */}
      <div className="mobile-joy-wrap">
        <div className="mobile-joy-base" ref={baseRef} style={{ transition: 'box-shadow 0.2s ease' }}>
          <div className="mobile-joy-handle" ref={handleRef} />
          <div className="mobile-joy-ring" />
        </div>
        <div className="mobile-joy-label">STEER</div>
      </div>

      {/* Throttle lever — right */}
      <div className="mobile-throttle-wrap">
        <div className="mobile-throttle-label">THR</div>
        <div className="mobile-throttle-track" ref={leverRef} style={{ transition: 'box-shadow 0.2s ease' }}>
          <div className="mobile-throttle-groove" />
          {/* Tick marks */}
          {[0, 25, 50, 75, 100].map(v => (
            <div key={v} className="mobile-throttle-tick" style={{ bottom: `${v}%` }}>
              <span>{v}</span>
            </div>
          ))}
          <div className="mobile-throttle-knob" ref={knobRef} />
        </div>
      </div>
    </div>
  );
};

export default MobileControls;